import React, {useEffect, useState} from 'react';
import {Dialog, DialogBackdrop, DialogPanel} from "@headlessui/react";
import {FaExternalLinkAlt} from "react-icons/fa";
import {FaLocationDot, FaXmark} from "react-icons/fa6";
import {Card} from "../../../../components/card/card.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {getIndividualImageUrl, getPassageImageUrl, NO_IMAGE, USER_IMAGE} from "../utils/individual-passage-search-utils.js";

const formatEventDate = (value) => value ? projectUtils.formatDate(value, "DD/MM/YYYY hh:mm").replace(",", "") : "";

const PassageDetail = ({eventSelected, target, searchedImage, setEventSelected}) => {
    const navigate = navigateHook();

    const [expandedModal, setExpandedModal] = useState(false);
    const [individualImg, setIndividualImg] = useState(undefined);

    useEffect(() => {
        if (!eventSelected) return;

        const imgUrl = eventSelected.individualId ? getIndividualImageUrl(eventSelected.individualId) : USER_IMAGE;

        const img = new Image();
        img.onload = () => setIndividualImg(imgUrl);
        img.onerror = () => setIndividualImg(NO_IMAGE);
        img.src = imgUrl;
    }, [eventSelected]);

    const openGis = () => {
        const url = `/gis?latLng=${eventSelected?.latitude},${eventSelected?.longitude}`;
        navigate(url, true);
    };

    const openMonitoring = () => {
        const url = `/individual-monitoring/form/${eventSelected?.individualId}`;
        navigate(url, true);
    };

    const showRegisteredImage = eventSelected?.individualId || target === "image";
    const registeredImage = target === "image" ? searchedImage : individualImg;
    const registeredLabel = target === "image" ? "Pesquisa" : "Cadastro";

    return (
        <>
            <Card className={"ips-default-max-height self-start w-[25%]"}>
                <Card.Body>
                    <div className={"bg-white rounded-[0.5rem] border border-[#ededed] mb-2"}>
                        <div className={"bg-white px-[10px] py-[7px] border-b border-gray-300"}>
                            <h5 className={"m-0 font-semibold text-[#676a6c] flex items-center w-full " + ((eventSelected.cameraName || eventSelected.passagePlace) ? "justify-between" : "justify-end")}>
                                <div className={"flex gap-2 items-center"}>
                                    {
                                        eventSelected?.latitude && eventSelected?.longitude &&
                                        <span className={"cursor-pointer"} title={"Abrir mapa em tempo real"} onClick={openGis}>
                                            <FaLocationDot/>
                                        </span>
                                    }
                                    {eventSelected?.cameraName ?? eventSelected?.passagePlace ?? ""}
                                </div>
                                <FaXmark className={"ips-hide-description"} onClick={() => setEventSelected(undefined)}/>
                            </h5>
                        </div>

                        <div className={"ips-body-padding"}>
                            <h4 className={"text-center"}>
                                {formatEventDate(eventSelected?.eventDateTime)}
                            </h4>

                            {
                                eventSelected?.individualWatchlists &&
                                <h4 className={"text-center text-red-600"}>{eventSelected.individualWatchlists.join(", ")}</h4>
                            }

                            <div className={"flex gap-2 mt-2 w-full"}>
                                {
                                    eventSelected.imagePath &&
                                    <div className={"text-center w-full"}>
                                        <div
                                            className={"ips-individual-image"}
                                            onClick={() => setExpandedModal(true)}
                                            style={{backgroundImage: `url(${getPassageImageUrl(eventSelected.imagePath)})`}}
                                        />
                                        <p className={"m-0 self-end"}>Passagem</p>
                                    </div>
                                }

                                {
                                    showRegisteredImage &&
                                    <div className={"text-center w-full"}>
                                        <div
                                            className={"ips-individual-image"}
                                            onClick={() => setExpandedModal(true)}
                                            style={{backgroundImage: `url(${registeredImage})`}}
                                        />
                                        <p className={"m-0 self-end"}>{registeredLabel}</p>
                                    </div>
                                }
                            </div>

                            {
                                eventSelected?.snapshot &&
                                <div className={"text-center w-full"}>
                                    <div
                                        className={"ips-individual-image"}
                                        onClick={() => setExpandedModal(true)}
                                        style={{backgroundImage: `url(${eventSelected?.snapshot?.snapshotUrl})`}}
                                    />
                                    <p className={"m-0 self-end"}>Quadro completo</p>
                                </div>
                            }

                            {
                                eventSelected?.confidence > 0 &&
                                <div className={"text-center"}>
                                    <span className={"ips-confidence"}>{(eventSelected.confidence * 100).toFixed(2) + "%"}</span>
                                </div>
                            }
                        </div>
                    </div>

                    {
                        eventSelected?.individualId &&
                        <div className={"bg-white rounded-[0.5rem] border border-[#ededed]"}>
                            <div className={"bg-white px-[10px] py-[7px] border-b border-gray-300"}>
                                <h5 className={"m-0 font-semibold text-[#676a6c] flex gap-2 items-center"}>
                                    Dados do monitoramento

                                    <span className={"cursor-pointer"} onClick={openMonitoring} title={"Abrir o cadastro"}>
                                        <FaExternalLinkAlt/>
                                    </span>
                                </h5>
                            </div>
                            <div className={"p-[10px]"}>
                                <p className={"m-0"}>
                                    Nome: <b>{eventSelected?.individualName ?? ""}</b>
                                </p>
                                <p className={"m-0"}>
                                    CPF: <b>{eventSelected?.individualCpf ? projectUtils.formatCpfToView(eventSelected?.individualCpf) : ""}</b>
                                </p>
                                <p className={"m-0"}>
                                    Data de nascimento: <b>{eventSelected?.individualBirthDate ? projectUtils.formatDate(eventSelected.individualBirthDate + " 00:00", "DD/MM/YYYY") : ""}</b>
                                </p>
                            </div>
                        </div>
                    }

                    {
                        eventSelected?.individualDescription &&
                        <div className={"bg-white rounded-[0.5rem] border border-[#ededed] mt-2"}>
                            <div className={"bg-white px-[10px] py-[7px] border-b border-gray-300"}>
                                <h5 className={"m-0 font-semibold text-[#676a6c] flex gap-2 items-center"}>
                                    Observação do monitoramento
                                </h5>
                            </div>
                            <div className={"p-[10px]"}>
                                {eventSelected.individualDescription}
                            </div>
                        </div>
                    }

                    {
                        eventSelected?.passagePlace &&
                        <div className={"bg-white rounded-[0.5rem] border border-[#ededed] mt-2"}>
                            <div className={"bg-white px-[10px] py-[7px] border-b border-gray-300"}>
                                <h5 className={"m-0 font-semibold text-[#676a6c] flex gap-2 items-center"}>
                                    Dados da passagem
                                </h5>
                            </div>
                            <div className={"p-[10px]"}>
                                <p className={"m-0"}>
                                    Nome: <b>{eventSelected?.passageName ?? ""}</b>
                                </p>
                                <p className={"m-0"}>
                                    CPF: <b>{eventSelected?.passageCpf ? projectUtils.formatCpfToView(eventSelected?.passageCpf) : ""}</b>
                                </p>
                                <p className={"m-0"}>
                                    Provedor: <b>{eventSelected?.provider ?? ""}</b>
                                </p>
                                <p className={"m-0"}>
                                    Local: <b>{eventSelected?.passagePlace ?? ""}</b>
                                </p>
                            </div>
                        </div>
                    }
                </Card.Body>
            </Card>

            <Dialog open={expandedModal} onClose={setExpandedModal} className={"relative z-[9999]"}>
                <DialogBackdrop
                    transition
                    className={"fixed inset-0 bg-black opacity-50 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"}
                />

                <div className={"fixed inset-0 flex w-screen items-center justify-center p-4"} onClick={() => setExpandedModal(false)}>
                    <DialogPanel className={"w-11/12 h-[90%] flex flex-row gap-2 items-center justify-center"} onClick={() => setExpandedModal(false)}>
                        {
                            eventSelected?.imagePath &&
                            <div className={"text-center h-full w-full content-center"}>
                                <div className={"ips-expanded-img"} style={{backgroundImage: `url(${getPassageImageUrl(eventSelected.imagePath)})`}}/>
                                <p className={"m-0 self-end text-white"}>Passagem</p>
                            </div>
                        }

                        {
                            showRegisteredImage &&
                            <div className={"text-center h-full w-full content-center"}>
                                <div className={"ips-expanded-img"} style={{backgroundImage: `url(${registeredImage})`}}/>
                                <p className={"m-0 self-end text-white"}>{registeredLabel}</p>
                            </div>
                        }

                        {
                            eventSelected?.snapshot &&
                            <div className={"text-center h-full w-full content-center"}>
                                <div className={"ips-expanded-img"} style={{backgroundImage: `url(${eventSelected?.snapshot?.snapshotUrl})`}}/>
                                <p className={"m-0 self-end text-white"}>Quadro completo</p>
                            </div>
                        }
                    </DialogPanel>
                </div>
            </Dialog>
        </>
    );
};

export default PassageDetail;
